import { useState } from "react";
import { View, Text, TextInput, Pressable, ActivityIndicator } from "react-native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { useRouter } from "expo-router";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { createTrainingSeries } from "@/lib/services";
import { colors, radius } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import { useAlert } from "@/hooks/useAlert";
import AlertSheet from "@/components/AlertSheet";
import { DateInput, TimeInput } from "@/components/DateTimeInputs";
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function CreateTrainingScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const { club } = useClub();
  const { alertProps, alert } = useAlert();

  const [title, setTitle] = useState("");
  const [dayOfWeek, setDayOfWeek] = useState(2);
  const [time, setTime] = useState("19:00");
  const [duration, setDuration] = useState("90");
  const [location, setLocation] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [fee, setFee] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!club || !user) return;
    if (!title.trim() || !startDate.trim() || !endDate.trim() || !location.trim()) {
      await alert({ title: "Missing fields", message: "Title, location, start and end dates are required." });
      return;
    }
    if (endDate < startDate) {
      await alert({ title: "Invalid dates", message: "End date must be after start date." });
      return;
    }
    setSaving(true);
    try {
      await createTrainingSeries(club.id, {
        title: title.trim(),
        dayOfWeek,
        time,
        duration: parseInt(duration) || 90,
        startDate,
        endDate,
        location: location.trim(),
        fee: Math.round((parseFloat(fee) || 0) * 100),
        clubName: club.name,
        createdBy: user.uid,
        availabilityLeadDays: club.availabilityLeadDays ?? 7,
      });
      router.back();
    } catch (err: any) {
      await alert({ title: "Error", message: err?.message || "Failed to create training." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <KeyboardAwareScroll style={{ flex: 1, backgroundColor: colors.bg }}>
      <ContentWidth>
        <View style={{ padding: 20 }}>
          <Label>Title</Label>
          <Input value={title} onChangeText={setTitle} placeholder="e.g. Winter Nets" />

          <Label>Day of week</Label>
          <View style={{ flexDirection: "row", gap: 6, flexWrap: "wrap" }}>
            {DAYS.map((d, i) => {
              const active = dayOfWeek === i;
              return (
                <Pressable
                  key={i}
                  onPress={() => setDayOfWeek(i)}
                  style={({ pressed }) => ({
                    paddingHorizontal: 14,
                    paddingVertical: 10,
                    borderRadius: radius.md,
                    borderWidth: 1.5,
                    borderColor: active ? colors.brand : colors.line,
                    backgroundColor: active ? colors.brand50 : colors.white,
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <Text style={{ fontSize: 13, fontWeight: "700", color: active ? colors.brandDark : colors.inkMuted }}>
                    {d}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          <View style={{ flexDirection: "row", gap: 12 }}>
            <View style={{ flex: 1 }}>
              <Label>Time</Label>
              <TimeInput value={time} onChange={setTime} />
            </View>
            <View style={{ flex: 1 }}>
              <Label>Duration (min)</Label>
              <Input value={duration} onChangeText={setDuration} placeholder="90" keyboardType="number-pad" />
            </View>
          </View>

          <Label>Location</Label>
          <Input value={location} onChangeText={setLocation} placeholder="e.g. Bushfield Indoor" />

          <Label>Start date</Label>
          <DateInput value={startDate} onChange={setStartDate} />

          <Label>End date</Label>
          <DateInput value={endDate} onChange={setEndDate} />

          <Label>Fee per session (£, 0 for free)</Label>
          <Input value={fee} onChangeText={setFee} placeholder="0" keyboardType="decimal-pad" />

          <Pressable
            onPress={handleSave}
            disabled={saving}
            style={({ pressed }) => ({
              backgroundColor: colors.brand,
              borderRadius: radius.lg,
              padding: 16,
              alignItems: "center",
              marginTop: 24,
              opacity: pressed ? 0.9 : 1,
            })}
          >
            {saving ? <ActivityIndicator color="#fff" /> : <Text style={{ color: "#fff", fontSize: 16, fontWeight: "700" }}>Create training series</Text>}
          </Pressable>

          <Text style={{ fontSize: 12, color: colors.inkMuted, textAlign: "center", marginTop: 12 }}>
            One session will be created for each {DAYS[dayOfWeek]} between the start and end dates.
          </Text>
        </View>
      </ContentWidth>
      <AlertSheet {...alertProps} />
    </KeyboardAwareScroll>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <Text style={{ fontSize: 12, fontWeight: "800", color: colors.inkMuted, textTransform: "uppercase", letterSpacing: 1, marginTop: 16, marginBottom: 8 }}>
      {children}
    </Text>
  );
}

function Input(props: React.ComponentProps<typeof TextInput>) {
  return (
    <TextInput
      placeholderTextColor={colors.inkMuted}
      {...props}
      style={[
        {
          backgroundColor: colors.white,
          borderRadius: radius.md,
          borderWidth: 1,
          borderColor: colors.line,
          paddingHorizontal: 14,
          paddingVertical: 12,
          fontSize: 15,
          color: colors.ink,
        },
        props.style as any,
      ]}
    />
  );
}
